import axios from 'axios';
import { useEffect, useState, useRef } from 'react';
import { baseURL, generateFacilityReport } from '../../../../helpers/api';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useTheme,
  TextField,
  Chip,
} from '@mui/material';
import {
  FilterList,
  VisibilityOutlined,
  PictureAsPdfOutlined,
  ArticleOutlined,
  AnalyticsOutlined,
  AssignmentTurnedInOutlined,
} from '@mui/icons-material';
import { getYears } from '../../../../helpers/dateHelpers';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { reportTypes, userCats } from '../../../../helpers/appstate';

// Recharts components
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from 'recharts';
import AddRecommendationModal from '../../result/result-view/AddRecommendationModal';

// preset: { facilityId, facilityName, year, quarter } opens straight onto that report.
// embedded: hides the selection sidebar (used inside the facility page's Reports tab).
export default function AnalyticalReport2({ props, preset = null, embedded = false }) {
  const { userId, userCategory } = props || {};

  const theme = useTheme();
  const reportRef = useRef(null);

  const initFacility = { facilityId: '', facilityName: '' };
  const [facilityList, setFacilityList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(
    preset?.facilityId
      ? { facilityId: preset.facilityId, facilityName: preset.facilityName || '' }
      : initFacility
  );
  const [yearSelected, setYearSelected] = useState(preset?.year || new Date().getFullYear());
  const [quarterList, setQuarterList] = useState([]);
  const [quarterSelected, setQuarterSelected] = useState(preset?.quarter || '');
  // const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Facility Metadata & Report Data States
  const [facilityDetailData, setFacilityDetailData] = useState(null);
  const [facilityPersonnelData, setFacilityPersonnelData] = useState(null);
  const [samplePointData, setSamplePointData] = useState(null);
  const [polutantData, setPolutantData] = useState([]);
  const [polutant24HrData, setPolutant24HrData] = useState([]);
  const [noiceLevelData, setNoiceLevelData] = useState([]);
  const [boreholeData, setBoreholeData] = useState([]);
  const [generatorData, setGeneratorData] = useState([]);
  const [boilerData, setBoilerData] = useState([]);
  const [reportDiscussion, setReportDiscussion] = useState('');
  const [analyticsData, setAnalyticsData] = useState([]);
  const [recommendationData, setRecommendationData] = useState([]);

  // Analytics Bar Palette
  const ANALYTICS_BAR_COLORS = {
    'Category Average': '#42a5f5',
    'NESREA Limit': '#9ccc65',
    'Sector Average': '#ab47bc',
    'State-wide Average': '#26c6da',
    DEFAULT: '#ef5350',
  };

  useEffect(() => {
    if (!embedded) fetchFacility();
    // With a preset facility/year/quarter, load the report straight away
    if (preset?.facilityId && preset?.year && preset?.quarter) handlePreviewReport();
  }, []);

  useEffect(() => {
    if (facilitySelected?.facilityId) {
      fetchQuarters();
    } else {
      setQuarterList([]);
      setQuarterSelected('');
    }
  }, [facilitySelected, yearSelected]);

  const fetchFacility = async () => {
    setFacilityLoading(true);
    try {
      const url = `${baseURL}/TelerikReports/GetFacilityList/${userId}`;
      const res = await axios.get(url);
      const { status, data } = res;
      if (status === 200) {
        setFacilityList(data || []);
      }
    } catch (error) {
      console.error('Error fetching facilities:', error);
    } finally {
      setFacilityLoading(false);
    }
  };

  const fetchQuarters = async () => {
    try {
      const res = await generateFacilityReport(facilitySelected?.facilityId, yearSelected);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setQuarterList(data || []);
      }
    } catch (error) {
      console.error('Error fetching quarters:', error);
    }
  };

  const handlePreviewReport = async () => {
    if (!facilitySelected?.facilityId || !yearSelected || !quarterSelected) return;

    setReportLoading(true);
    try {
      // 1. Facility Details (Path preserved)
      const facilityDetailUrl = `${baseURL}/TelerikReports/TelerikFacilityDetails/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const facilityDetailRes = await fetch(facilityDetailUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const facilityDetailResData = await facilityDetailRes.json();

      const detailsObj = facilityDetailResData?.data || facilityDetailResData;
      setFacilityDetailData(detailsObj);
      setReportDiscussion(
        facilityDetailResData?.reportDiscussion || detailsObj?.reportDiscussion || ''
      );

      // 2. Facility Personnel (Path preserved)
      const facilityPersonnelUrl = `${baseURL}/TelerikReports/FacilityPersonels/${facilitySelected?.facilityId}`;
      const facilityPersonnelRes = await fetch(facilityPersonnelUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const facilityPersonnelResData = await facilityPersonnelRes.json();
      setFacilityPersonnelData(facilityPersonnelResData);

      // 3. Sample Points (Path preserved)
      const samplePointUrl = `${baseURL}/TelerikReports/GetSamplingPointsForFacility/${facilitySelected?.facilityId}`;
      const samplePointRes = await fetch(samplePointUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const samplePointResData = await samplePointRes.json();
      setSamplePointData(samplePointResData);

      // 4. Pollutants Data (Path preserved)
      const polutantUrl = `${baseURL}/TelerikReports/GetFacilitySamplingPointsPollutants/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const polutantRes = await fetch(polutantUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const polutantResData = await polutantRes.json();
      setPolutantData(polutantResData || []);
      // setReportData(polutantResData || []);

      // 4. Pollutants Data (Path preserved)
      const polutant24HrUrl = `${baseURL}/TelerikReports/GetFacilitySamplingPoints24HourPollutants/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const polutant24HrRes = await fetch(polutant24HrUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const polutant24HrResData = await polutant24HrRes.json();
      setPolutant24HrData(polutant24HrResData || []);
      //setReportData(polutant24HrResData || []);

      // 5. Ambient Noise Level Data (Path preserved)
      const noiceLevelUrl = `${baseURL}/TelerikReports/GetFacilitySamplingPointsAmbientNoiseLevel/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const noiceLevelRes = await fetch(noiceLevelUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const noiceLevelResData = await noiceLevelRes.json();
      setNoiceLevelData(noiceLevelResData || []);

      // 6. Borehole Data (Path preserved)
      const boreholeUrl = `${baseURL}/TelerikReports/GetFacilitySamplingPointsBoreholeMeasurement/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const boreholeRes = await fetch(boreholeUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const boreholeResData = await boreholeRes.json();
      setBoreholeData(boreholeResData || []);

      // 7. Generator Stack Emissions (Path preserved)
      const generatorUrl = `${baseURL}/TelerikReports/GetFacilityGeneratorStackEmission/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const generatorRes = await fetch(generatorUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const generatorResData = await generatorRes.json();
      setGeneratorData(generatorResData || []);

      // 8. Boiler Stack Emissions (Path preserved)
      const boilerUrl = `${baseURL}/TelerikReports/GetFacilityBoilerStackEmission/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const boilerRes = await fetch(boilerUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const boilerResData = await boilerRes.json();
      setBoilerData(boilerResData || []);

      // 9. Facility Analytics (Path preserved)
      const analyticsUrl = `${baseURL}/TelerikReports/GetFacilityAnalyticsReport/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}/1`;
      const analyticsRes = await fetch(analyticsUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const analyticsResData = await analyticsRes.json();
      setAnalyticsData(analyticsResData?.data || analyticsResData || []);

      // 10. Report Recommendations (Path preserved)
      const recommendationUrl = `${baseURL}/TelerikReports/GetReportRecommendations/${facilitySelected?.facilityId}/${quarterSelected}/${yearSelected}`;
      const recommendationRes = await fetch(recommendationUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const recommendationResData = await recommendationRes.json();
      setRecommendationData(recommendationResData?.data || recommendationResData || []);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const getResultValue = (point, paramId, paramMatch) => {
    if (!point?.results || !Array.isArray(point.results)) return '-';

    const allDataItems = point.results.flatMap((resObj) => resObj?.data || []);

    const resMatch = allDataItems.find((r) => {
      const matchesPoint =
        r.samplePointId === point.samplePointId ||
        (r.samplePoint &&
          point.samplePointName &&
          r.samplePoint.trim().toLowerCase() === point.samplePointName.trim().toLowerCase());

      const matchesParam =
        r.parameterId === paramId ||
        (r.measurementParameterId &&
          paramMatch?.measurementParameterId &&
          String(r.measurementParameterId) === String(paramMatch.measurementParameterId));

      return matchesPoint && matchesParam;
    });

    if (
      resMatch &&
      resMatch.resultValue !== undefined &&
      resMatch.resultValue !== null &&
      resMatch.resultValue !== ''
    ) {
      return resMatch.resultValue;
    }

    return '-';
  };

  const getResultObject = (point, paramId, paramMatch) => {
    if (!point?.results || !Array.isArray(point.results)) return null;
    const allDataItems = point.results.flatMap((resObj) => resObj?.data || []);
    return allDataItems.find((r) => {
      const matchesPoint =
        r.samplePointId === point.samplePointId ||
        (r.samplePoint &&
          point.samplePointName &&
          r.samplePoint.trim().toLowerCase() === point.samplePointName.trim().toLowerCase());

      const matchesParam =
        r.parameterId === paramId ||
        (r.measurementParameterId &&
          paramMatch?.measurementParameterId &&
          String(r.measurementParameterId) === String(paramMatch.measurementParameterId));

      return matchesPoint && matchesParam;
    });
  };

  const checkIfExceeded = (point, paramId, paramMatch) => {
    const resObj = getResultObject(point, paramId, paramMatch);
    if (!resObj) return false;

    const rawVal = resObj.resultValue;
    const numVal =
      rawVal !== undefined && rawVal !== null && !isNaN(rawVal) ? Number(rawVal) : null;
    const nesreaLimit = parseFloat(paramMatch?.nesrea);
    const limitVal = !isNaN(nesreaLimit) ? nesreaLimit : null;
    const status = resObj.resultStatus || 'UNKNOWN';

    return (
      status === 'RECHECK' ||
      status === 'EXCEEDED' ||
      (limitVal !== null && numVal !== null && numVal > limitVal)
    );
  };

  // PDF layout settings
  const PDF_MARGIN_MM = 15; // standard margin on all sides
  const PDF_RENDER_WIDTH_PX = 850; // fixed layout width so output is identical on any screen size
  const PDF_SCALE = 2;

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Load a (possibly cross-origin) image as a data URL so html2canvas can paint it.
  // `no-store` / cache-busting matters: if the browser re-uses the copy it already cached
  // for the <img> tag (fetched without CORS), the CORS request fails and the logo is dropped.
  const imageToDataUrl = async (src) => {
    try {
      const res = await fetch(src, { mode: 'cors', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (fetchError) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            canvas.getContext('2d').drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } catch (e) {
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = `${src}${src.includes('?') ? '&' : '?'}pdfcb=${Date.now()}`;
      });
    }
  };

  // Swap every <img> inside the element to a data URL; returns a function that restores them
  const inlineImages = async (element) => {
    const images = Array.from(element.querySelectorAll('img'));
    const originals = images.map((img) => img.getAttribute('src'));

    await Promise.all(
      images.map(async (img) => {
        if (!img.src || img.src.startsWith('data:')) return;
        const dataUrl = await imageToDataUrl(img.src);
        if (!dataUrl) {
          console.warn('PDF export: could not load image (CORS?)', img.src);
          return;
        }
        img.src = dataUrl;
        try {
          await img.decode();
        } catch (e) {
          // ignore - html2canvas will still try to draw it
        }
      })
    );

    return () => images.forEach((img, i) => img.setAttribute('src', originals[i]));
  };

  // Collect layout info (in canvas pixels) used to choose clean page breaks
  const measurePdfLayout = (element, ratio) => {
    const rootTop = element.getBoundingClientRect().top;
    const toPx = (y) => (y - rootTop) * ratio;

    // Blocks that must never be cut in half by a page break
    const atomicRanges = Array.from(
      element.querySelectorAll(
        'tr, img, p, li, h1, h2, h3, h4, h5, h6, .MuiTypography-root, [data-pdf-avoid-break]'
      )
    ).map((el) => {
      const r = el.getBoundingClientRect();
      return { top: toPx(r.top), bottom: toPx(r.bottom) };
    });

    const tables = Array.from(element.querySelectorAll('table'))
      .map((table) => {
        const thead = table.querySelector('thead');
        const firstRow = table.querySelector('tbody tr');
        const tableRect = table.getBoundingClientRect();
        const headRect = thead?.getBoundingClientRect();

        // Keep the section title + table header + first row together
        const container = table.closest('.MuiTableContainer-root') || table;
        const title = container.previousElementSibling;
        const keepTop = title?.classList.contains('MuiTypography-root')
          ? title.getBoundingClientRect().top
          : tableRect.top;
        const keepBottom = firstRow
          ? firstRow.getBoundingClientRect().bottom
          : headRect?.bottom || tableRect.bottom;
        atomicRanges.push({ top: toPx(keepTop), bottom: toPx(keepBottom) });

        if (!thead) return null;
        return {
          headTop: toPx(headRect.top),
          headBottom: toPx(headRect.bottom),
          tableBottom: toPx(tableRect.bottom),
        };
      })
      .filter(Boolean);

    return { atomicRanges, tables };
  };

  // Move the break upwards until it doesn't fall inside any atomic block
  const findPageBreak = (pageTop, idealBottom, atomicRanges) => {
    const minBottom = pageTop + (idealBottom - pageTop) * 0.5; // never leave a page less than half full
    let y = idealBottom;
    for (;;) {
      const crossing = atomicRanges.filter((r) => r.top < y - 1 && r.bottom > y + 1);
      if (crossing.length === 0) break;
      const nextY = Math.min(...crossing.map((r) => r.top));
      if (nextY <= minBottom) return idealBottom; // block taller than a page - hard cut
      y = nextY;
    }
    return y;
  };

  // Multi-Page PDF Exporter (A4, margins, smart page breaks, repeated table headers)
  const exportToPDF = async () => {
    const element = reportRef.current;
    if (!element) return;
    setDownloadLoading(true);

    const prevStyle = { width: element.style.width, padding: element.style.padding };
    let restoreImages = () => {};

    try {
      // Lay the report out at a fixed width without its on-screen padding (PDF margins replace it)
      element.style.width = `${PDF_RENDER_WIDTH_PX}px`;
      element.style.padding = '0px';

      restoreImages = await inlineImages(element);
      await wait(500); // let Recharts' ResponsiveContainer re-measure at the new width

      const canvas = await html2canvas(element, {
        scale: PDF_SCALE,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const ratio = canvas.width / element.getBoundingClientRect().width;
      const { atomicRanges, tables } = measurePdfLayout(element, ratio);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidthMm = pdf.internal.pageSize.getWidth();
      const pageHeightMm = pdf.internal.pageSize.getHeight();
      const contentWidthMm = pageWidthMm - PDF_MARGIN_MM * 2;
      const contentHeightMm = pageHeightMm - PDF_MARGIN_MM * 2;
      const pxPerMm = canvas.width / contentWidthMm;
      const pageHeightPx = Math.floor(contentHeightMm * pxPerMm);

      let y = 0;
      let pageIndex = 0;
      while (y < canvas.height - 1) {
        // If this page starts in the middle of a table body, repeat that table's header
        const tableInProgress =
          pageIndex > 0 && tables.find((t) => t.headBottom <= y + 1 && t.tableBottom > y + 2);
        const headerHeight = tableInProgress
          ? Math.round(tableInProgress.headBottom - tableInProgress.headTop)
          : 0;

        const available = pageHeightPx - headerHeight;
        const sliceEnd =
          y + available >= canvas.height
            ? canvas.height
            : Math.round(findPageBreak(y, y + available, atomicRanges));
        const sliceHeight = sliceEnd - y;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = headerHeight + sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        if (tableInProgress) {
          ctx.drawImage(
            canvas,
            0, Math.round(tableInProgress.headTop), canvas.width, headerHeight,
            0, 0, canvas.width, headerHeight
          );
        }
        ctx.drawImage(
          canvas,
          0, y, canvas.width, sliceHeight,
          0, headerHeight, canvas.width, sliceHeight
        );

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(
          pageCanvas.toDataURL('image/png'),
          'PNG',
          PDF_MARGIN_MM,
          PDF_MARGIN_MM,
          contentWidthMm,
          pageCanvas.height / pxPerMm,
          undefined,
          'FAST'
        );

        y = sliceEnd;
        pageIndex += 1;
      }

      // Page numbers in the bottom margin
      const totalPages = pdf.getNumberOfPages();
      pdf.setFontSize(8);
      pdf.setTextColor(120);
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidthMm / 2, pageHeightMm - PDF_MARGIN_MM / 2, {
          align: 'center',
        });
      }

      pdf.save(
        `${facilitySelected.facilityName ||
          'Facility'}_Report_${yearSelected}_Q${quarterSelected}.pdf`
      );
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      restoreImages();
      element.style.width = prevStyle.width;
      element.style.padding = prevStyle.padding;
      setDownloadLoading(false);
    }
  };

  const groupDataByMeasurement = (data) => {
    if (!Array.isArray(data)) return {};
    return data.reduce((acc, curr) => {
      const group = curr.groupName || 'General';
      if (!acc[group]) acc[group] = [];
      acc[group].push(curr);
      return acc;
    }, {});
  };

  // Dynamic Table Renderer
  const renderDynamicPollutantTable = (title, dataList, customFootnotes = null) => {
    if (!dataList || dataList.length === 0) return null;

    const samplePollutantObj = dataList[0]?.pollutants || {};
    const pollutantKeys = Object.keys(samplePollutantObj);

    const gaseousKeys = pollutantKeys.filter((key) =>
      ['CO', 'NH3', 'NO', 'NO2', 'NOx', 'O3', 'SO2', 'H2S', 'CO2', 'VOCs', 'TVOCs'].some((g) =>
        key.toLowerCase().includes(g.toLowerCase())
      )
    );

    const particulateKeys = pollutantKeys.filter(
      (key) =>
        key.toLowerCase().includes('pm') ||
        key.toLowerCase().includes('tsp') ||
        key.toLowerCase().includes('spm')
    );

    const otherKeys = pollutantKeys.filter(
      (key) => !gaseousKeys.includes(key) && !particulateKeys.includes(key)
    );

    const hasCategorizedColumns = gaseousKeys.length > 0 || particulateKeys.length > 0;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 0.8, fontSize: '0.75rem', letterSpacing: '0.2px' }}
        >
          {title}
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: '1px solid #d0d0d0',
              '& .MuiTableCell-root': {
                border: '1px solid #d0d0d0',
                py: 0.3,
                px: 0.75,
                fontSize: '0.7rem',
                fontFamily: '"Calibri", "Arial", sans-serif',
              },
            }}
          >
            <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: '#4a7bb0' } }}>
              {hasCategorizedColumns ? (
                <>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}>
                      Monitoring Stations
                    </TableCell>
                    {gaseousKeys.length > 0 && (
                      <TableCell
                        colSpan={gaseousKeys.length}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        Gaseous Concentration (ppm)
                      </TableCell>
                    )}
                    {particulateKeys.length > 0 && (
                      <TableCell
                        colSpan={particulateKeys.length}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        Particulates (µg/m³)
                      </TableCell>
                    )}
                    {otherKeys.map((key) => (
                      <TableCell
                        key={key}
                        rowSpan={2}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: key }} />
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    {gaseousKeys.map((key) => (
                      <TableCell
                        key={key}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: key }} />
                      </TableCell>
                    ))}
                    {particulateKeys.map((key) => (
                      <TableCell
                        key={key}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: key }} />
                      </TableCell>
                    ))}
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}>
                    Monitoring Stations
                  </TableCell>
                  {pollutantKeys.map((key) => (
                    <TableCell key={key} align="center" sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}>
                      <span dangerouslySetInnerHTML={{ __html: key }} />
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {dataList.map((row, idx) => {
                const isSummaryRow = [
                  'minimum',
                  'maximum',
                  'average',
                  'nesrea limit',
                  'compliance (yes/no)',
                  'regulatory standardᵃ',
                ].includes(row.monitoringStation?.trim().toLowerCase());

                const isComplianceRow =
                  row.monitoringStation?.trim().toLowerCase() === 'compliance (yes/no)';

                const orderedKeys = hasCategorizedColumns
                  ? [...gaseousKeys, ...particulateKeys, ...otherKeys]
                  : pollutantKeys;

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      bgcolor: isComplianceRow ? 'grey.200' : isSummaryRow ? 'grey.50' : 'inherit',
                    }}
                  >
                    <TableCell sx={{ fontWeight: isSummaryRow ? 700 : 500, fontSize: '0.7rem' }}>
                      {row.monitoringStation}
                    </TableCell>
                    {orderedKeys.map((key) => {
                      const val = row.pollutants?.[key] || '-';
                      const isNo =
                        String(val)
                          .trim()
                          .toUpperCase() === 'NO';

                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isNo ? '#d32f2f' : 'inherit',
                            fontWeight: isNo || isSummaryRow ? 700 : 400,
                            bgcolor: isNo ? alpha('#d32f2f', 0.08) : 'transparent',
                            fontSize: '0.7rem',
                          }}
                        >
                          {val}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {customFootnotes && (
          <Box sx={{ mt: 0.8, pl: 0.5 }}>
            {customFootnotes.map((fn, idx) => (
              <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {fn}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const renderDynamicPollutant24HrTable = (title, dataList, customFootnotes = null) => {
    if (!dataList || dataList.length === 0) return null;

    const samplePollutantObj = dataList[0]?.pollutants || {};
    const pollutantKeys = Object.keys(samplePollutantObj);

    const gaseousKeys = pollutantKeys.filter((key) =>
      ['CO', 'NH3', 'NO', 'NO2', 'NOx', 'O3', 'SO2', 'H2S', 'CO2', 'VOCs', 'TVOCs'].some((g) =>
        key.toLowerCase().includes(g.toLowerCase())
      )
    );

    const particulateKeys = pollutantKeys.filter(
      (key) =>
        key.toLowerCase().includes('pm') ||
        key.toLowerCase().includes('tsp') ||
        key.toLowerCase().includes('spm')
    );

    const otherKeys = pollutantKeys.filter(
      (key) => !gaseousKeys.includes(key) && !particulateKeys.includes(key)
    );

    const hasCategorizedColumns = gaseousKeys.length > 0 || particulateKeys.length > 0;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 0.8, fontSize: '0.75rem', letterSpacing: '0.2px' }}
        >
          {title}
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
          <Table
            size="small"
            sx={{
              border: '1px solid #d0d0d0',
              '& .MuiTableCell-root': {
                border: '1px solid #d0d0d0',
                py: 0.3,
                px: 0.75,
                fontSize: '0.7rem',
                fontFamily: '"Calibri", "Arial", sans-serif',
              },
            }}
          >
            <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: '#4a7bb0' } }}>
              {hasCategorizedColumns ? (
                <>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}>
                      Monitoring Stations
                    </TableCell>
                    {gaseousKeys.length > 0 && (
                      <TableCell
                        colSpan={gaseousKeys.length}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        Gaseous Concentration (ppm)
                      </TableCell>
                    )}
                    {particulateKeys.length > 0 && (
                      <TableCell
                        colSpan={particulateKeys.length}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        Particulates (µg/m³)
                      </TableCell>
                    )}
                    {otherKeys.map((key) => (
                      <TableCell
                        key={key}
                        rowSpan={2}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: key }} />
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    {gaseousKeys.map((key) => (
                      <TableCell
                        key={key}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: key }} />
                      </TableCell>
                    ))}
                    {particulateKeys.map((key) => (
                      <TableCell
                        key={key}
                        align="center"
                        sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: key }} />
                      </TableCell>
                    ))}
                  </TableRow>
                </>
              ) : (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}>
                    Monitoring Stations
                  </TableCell>
                  {pollutantKeys.map((key) => (
                    <TableCell key={key} align="center" sx={{ fontWeight: 'bold', color: '#fff', fontSize: '0.7rem' }}>
                      <span dangerouslySetInnerHTML={{ __html: key }} />
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {dataList.map((row, idx) => {
                const isSummaryRow = [
                  'minimum',
                  'maximum',
                  'average',
                  'nesrea limit',
                  'compliance (yes/no)',
                  'regulatory standardᵃ',
                ].includes(row.monitoringStation?.trim().toLowerCase());

                const isComplianceRow =
                  row.monitoringStation?.trim().toLowerCase() === 'compliance (yes/no)';

                const orderedKeys = hasCategorizedColumns
                  ? [...gaseousKeys, ...particulateKeys, ...otherKeys]
                  : pollutantKeys;

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      bgcolor: isComplianceRow ? 'grey.200' : isSummaryRow ? 'grey.50' : 'inherit',
                    }}
                  >
                    <TableCell sx={{ fontWeight: isSummaryRow ? 700 : 500, fontSize: '0.7rem' }}>
                      {row.monitoringStation}
                    </TableCell>
                    {orderedKeys.map((key) => {
                      const val = row.pollutants?.[key] || '-';
                      const isNo =
                        String(val)
                          .trim()
                          .toUpperCase() === 'NO';

                      return (
                        <TableCell
                          key={key}
                          align="center"
                          sx={{
                            color: isNo ? '#d32f2f' : 'inherit',
                            fontWeight: isNo || isSummaryRow ? 700 : 400,
                            bgcolor: isNo ? alpha('#d32f2f', 0.08) : 'transparent',
                            fontSize: '0.7rem',
                          }}
                        >
                          {val}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {customFootnotes && (
          <Box sx={{ mt: 0.8, pl: 0.5 }}>
            {customFootnotes.map((fn, idx) => (
              <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {fn}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Recommendations & Corrective Actions Section Renderer
  const renderRecommendationsReportView = () => {
    if (
      !recommendationData ||
      !Array.isArray(recommendationData) ||
      recommendationData.length === 0
    ) {
      return null;
    }

    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AssignmentTurnedInOutlined color="primary" sx={{ fontSize: '1.1rem' }} />
          <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ fontSize: '0.85rem' }}>
            Recommendations & Corrective Actions
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, mb: 2 }}>
          <Table
            size="small"
            sx={{
              border: '1px solid #d0d0d0',
              '& .MuiTableCell-root': {
                border: '1px solid #d0d0d0',
                py: 0.5,
                px: 1,
                fontSize: '0.725rem',
                fontFamily: '"Calibri", "Arial", sans-serif',
                verticalAlign: 'top',
              },
            }}
          >
            <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: '#4a7bb0' } }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: '5%', fontSize: '0.725rem' }} align="center">
                  #
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: '40%', fontSize: '0.725rem' }}>
                  Recommendations / Comments
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: '35%', fontSize: '0.725rem' }}>
                  Corrective Actions
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff', width: '20%', fontSize: '0.725rem' }}>
                  Logged By
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recommendationData.map((rec, index) => {
                const commentHtml = rec?.comment || '-';
                const actionsHtml = rec?.actions || '-';
                const isActionEmpty =
                  !rec?.actions ||
                  rec?.actions.trim() === '<p><br></p>' ||
                  rec?.actions.trim() === '';

                return (
                  <TableRow key={rec.id || index} hover>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.725rem' }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          '& p': { m: 0, mb: 0.3, fontSize: '0.725rem', lineHeight: 1.4 },
                          '& ol, & ul': { pl: 1.8, m: 0 },
                          '& li': { fontSize: '0.725rem', mb: 0.2 },
                        }}
                        dangerouslySetInnerHTML={{ __html: commentHtml }}
                      />
                    </TableCell>
                    <TableCell>
                      {isActionEmpty ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic', fontSize: '0.7rem' }}
                        >
                          No specific action listed
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            '& p': { m: 0, mb: 0.3, fontSize: '0.725rem', lineHeight: 1.4 },
                            '& ol, & ul': { pl: 1.8, m: 0 },
                            '& li': { fontSize: '0.725rem', mb: 0.2 },
                          }}
                          dangerouslySetInnerHTML={{ __html: actionsHtml }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontWeight={700} display="block" sx={{ fontSize: '0.7rem' }}>
                        {rec.fullName || 'User'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.675rem' }}>
                        {rec.emailAddress || ''}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.3, fontSize: '0.65rem' }}
                      >
                        {rec.entryDate
                          ? new Date(rec.entryDate).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Facility Analytics Section Renderer
  const renderAnalyticsReportView = () => {
    if (!analyticsData || !Array.isArray(analyticsData) || analyticsData.length === 0) return null;

    return (
      <Box sx={{ mt: 4, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          {/* <AnalyticsOutlined color="primary" sx={{ fontSize: '1.1rem' }} /> */}
          <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ fontSize: '0.85rem' }}>
            Facility Analytics & Comparative Benchmarks
          </Typography>
        </Stack>

        {analyticsData.map((item, index) => {
          const { samplePointName, results, reportDiscussion: groupDiscussion } = item || {};
          if (!results || !Array.isArray(results) || results.length === 0) return null;

          const locationRows = results[0]?.location || [];

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{ p: 2, mb: 3, borderRadius: 1, bgcolor: '#ffffff' }}
            >
              {/* Green Header Bar */}
              <Box
                sx={{ bgcolor: '#d4edda', color: '#155724', py: 0.6, px: 1.5, borderRadius: 1, mb: 1.5 }}
              >
                <Typography variant="subtitle2" fontWeight={700} align="center" sx={{ fontSize: '0.775rem' }}>
                  {samplePointName}
                </Typography>
              </Box>

              {/* Comparative Parameters Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, mb: 2 }}>
                <Table
                  size="small"
                  sx={{
                    border: '1px solid #d0d0d0',
                    '& .MuiTableCell-root': {
                      border: '1px solid #d0d0d0',
                      py: 0.4,
                      px: 0.8,
                      fontSize: '0.7rem',
                      fontFamily: '"Calibri", "Arial", sans-serif',
                    },
                  }}
                >
                  <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: '#f8f9fa' } }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Location / Benchmark</TableCell>
                      {results.map((res, rIdx) => (
                        <TableCell key={rIdx} align="center" sx={{ fontWeight: 'bold' }}>
                          <span dangerouslySetInnerHTML={{ __html: res.parameter }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationRows.map((loc, lIdx) => (
                      <TableRow key={lIdx}>
                        <TableCell sx={{ fontWeight: loc.name === samplePointName ? 700 : 500 }}>
                          {loc.name}
                        </TableCell>
                        {results.map((res, rIdx) => {
                          const matchedLoc = res.location?.find((l) => l.name === loc.name);
                          const val = matchedLoc ? matchedLoc.count : '-';
                          return (
                            <TableCell key={rIdx} align="center">
                              {val}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* 4-In-Line Parameter Bar Charts */}
              <Grid2 container spacing={1.5} sx={{ mb: 2 }}>
                {results.map((res, rIdx) => {
                  const chartData = (res.location || []).map((l) => ({
                    name: l.name,
                    val: Number(l.count) || 0,
                  }));

                  return (
                    <Grid2 key={rIdx} size={{ xs: 12, sm: 6, md: 3 }}>
                      <Box
                        data-pdf-avoid-break
                        sx={{ border: '1px solid #e0e0e0', p: 0.8, borderRadius: 1, bgcolor: '#fff' }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          align="center"
                          display="block"
                          sx={{ mb: 0.5, fontSize: '0.675rem' }}
                        >
                          <span dangerouslySetInnerHTML={{ __html: res.parameter }} />
                        </Typography>
                        <Box sx={{ width: '100%', height: 160 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={chartData}
                              margin={{ top: 10, right: 10, left: -25, bottom: 35 }}
                            >
                              <XAxis dataKey="name" tick={false} />
                              <YAxis tick={{ fontSize: 8 }} />
                              <Tooltip
                                formatter={(v) => [
                                  `${v} ${res.parameterUnit || ''}`,
                                  res.parameterName,
                                ]}
                              />
                              <Bar dataKey="val">
                                {chartData.map((entry, cIdx) => (
                                  <Cell
                                    key={cIdx}
                                    fill={
                                      ANALYTICS_BAR_COLORS[entry.name] ||
                                      (entry.name === samplePointName
                                        ? '#d32f2f'
                                        : ANALYTICS_BAR_COLORS.DEFAULT)
                                    }
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </Box>
                        {/* Custom Legend */}
                        <Stack spacing={0.2} sx={{ mt: 0.8, px: 0.5 }}>
                          {chartData.map((entry, cIdx) => (
                            <Stack key={cIdx} direction="row" spacing={0.6} alignItems="center">
                              <Box
                                sx={{
                                  width: 7,
                                  height: 7,
                                  bgcolor:
                                    ANALYTICS_BAR_COLORS[entry.name] ||
                                    (entry.name === samplePointName
                                      ? '#d32f2f'
                                      : ANALYTICS_BAR_COLORS.DEFAULT),
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{ fontSize: '0.625rem', lineHeight: 1 }}
                              >
                                {entry.name}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Grid2>
                  );
                })}
              </Grid2>

              {/* Group Discussion Text */}
              {groupDiscussion && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: '#f9f9f9',
                    borderRadius: 1,
                    borderLeft: '3px solid #2e7d32',
                    '& p': { fontSize: '0.75rem', mb: 0.5, lineHeight: 1.4 },
                    '& b': { fontWeight: 700 },
                    '& i': { fontStyle: 'italic' },
                  }}
                  dangerouslySetInnerHTML={{ __html: groupDiscussion }}
                />
              )}
            </Paper>
          );
        })}
      </Box>
    );
  };

  const hasAnyReportData = Boolean(
    facilityDetailData ||
      (polutantData && polutantData.length > 0) ||
      (polutant24HrData && polutant24HrData.length > 0) ||
      (noiceLevelData && noiceLevelData.length > 0) ||
      (boreholeData && boreholeData.length > 0) ||
      (generatorData && generatorData.length > 0) ||
      (boilerData && boilerData.length > 0) ||
      (analyticsData && analyticsData.length > 0) ||
      (recommendationData && recommendationData.length > 0) 
      // || (reportData && reportData.length > 0)
  );

  const renderReportTables = () => {
    if (reportLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 10 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Generating report preview...
          </Typography>
        </Stack>
      );
    }

    if (!hasAnyReportData) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ height: '100%', color: 'text.secondary', py: 10 }}
        >
          <Typography variant="subtitle1">No Report Data</Typography>
          <Typography variant="caption">Select controls on the right and click "Preview".</Typography>
        </Stack>
      );
    }

    // const groupedData = groupDataByMeasurement(reportData);

    return (
      <Box
        ref={reportRef}
        sx={{
          p: 3,
          bgcolor: '#fff',
          minHeight: '100%',
          fontFamily: '"Calibri", "Arial", sans-serif',
        }}
      >
        {/* --- REPORT HEADER BANNER --- */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          data-pdf-avoid-break
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.25rem', lineHeight: 1.2 }}>
              I.E.M.S Report
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '0.85rem', mt: 0.5 }}>
              Quarter {quarterSelected || '2'}, {yearSelected || '2024'}
            </Typography>
          </Box>
          {facilityDetailData?.stateLogo && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Box
                component="img"
                src={'https://testapi.iems.ng/files/document/iems_new_icon.png'}
                alt="NesreaLogo"
                sx={{ height: 65, objectFit: 'contain' }}
              />
              <Box
                component="img"
                src={facilityDetailData.stateLogo}
                alt="Logo"
                sx={{ height: 65, objectFit: 'contain' }}
              />
            </Box>
          )}
        </Stack>

        <Box sx={{ textAlign: 'center', my: 2.5 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="success.main"
            sx={{
              textTransform: 'uppercase',
              fontSize: '0.95rem',
              display: 'block',
              letterSpacing: '0.5px',
            }}
          >
            NATIONAL ENVIRONMENTAL STANDARDS AND REGULATIONS ENFORCEMENT AGENCY
          </Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            color="error.main"
            sx={{
              mt: 0.5,
              textTransform: 'uppercase',
              fontSize: '1.1rem',
              letterSpacing: '0.6px',
            }}
          >
            INDUSTRIAL EMISSION MONITORING SCHEME (I.E.M.S)
          </Typography>
        </Box>

        {/* --- 1. FACILITY DETAILS TABLE --- */}
        {facilityDetailData && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8, fontSize: '0.75rem', letterSpacing: '0.2px' }}>
              Facility Details
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
              <Table
                size="small"
                sx={{
                  border: '1px solid #d0d0d0',
                  '& .MuiTableCell-root': {
                    border: '1px solid #d0d0d0',
                    py: 0.3,
                    px: 0.8,
                    fontSize: '0.7rem',
                    fontFamily: '"Calibri", "Arial", sans-serif',
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '25%', bgcolor: '#f5f5f5' }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {facilityDetailData.facilityName || '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Address</TableCell>
                    <TableCell>{facilityDetailData.address || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Sector</TableCell>
                    <TableCell>{facilityDetailData.sectorName || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Category</TableCell>
                    <TableCell>{facilityDetailData.facilityCategoryName || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>Region</TableCell>
                    <TableCell>{facilityDetailData.zoneName || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>State</TableCell>
                    <TableCell>{facilityDetailData.stateName || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>LGA</TableCell>
                    <TableCell>{facilityDetailData.lgaName || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                      Facility Coordinate
                    </TableCell>
                    <TableCell>
                      LAT: {facilityDetailData.coordinates?.latitude || '-'}, LNG:{' '}
                      {facilityDetailData.coordinates?.longitude || '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5' }}>
                      Report Date
                    </TableCell>
                    <TableCell>
                      {facilityDetailData.entryDate
                        ? new Date(facilityDetailData.entryDate).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })
                        : '-'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* --- 2. FACILITY PERSONNEL TABLE --- */}
        {(facilityPersonnelData || facilityDetailData?.facilityPersonel) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8, fontSize: '0.75rem' }}>
              Facility Personnel
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
              <Table
                size="small"
                sx={{
                  border: '1px solid #d0d0d0',
                  '& .MuiTableCell-root': {
                    border: '1px solid #d0d0d0',
                    py: 0.3,
                    px: 0.8,
                    fontSize: '0.7rem',
                    fontFamily: '"Calibri", "Arial", sans-serif',
                  },
                }}
              >
                <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: '#eee' } }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Designation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(facilityPersonnelData || facilityDetailData?.facilityPersonel || []).map(
                    (person, idx) => (
                      <TableRow key={person.personelId || idx}>
                        <TableCell sx={{ fontWeight: 600 }}>{person.fullName || '-'}</TableCell>
                        <TableCell>{person.email || '-'}</TableCell>
                        <TableCell>{person.phone || '-'}</TableCell>
                        <TableCell>{person.designation || '-'}</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* --- 3. SAMPLING POINTS DESCRIPTIONS TABLE --- */}
        {(samplePointData || facilityDetailData?.samplePoints) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.8, fontSize: '0.75rem' }}>
              Sampling Points Descriptions
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
              <Table
                size="small"
                sx={{
                  border: '1px solid #d0d0d0',
                  '& .MuiTableCell-root': {
                    border: '1px solid #d0d0d0',
                    py: 0.3,
                    px: 0.8,
                    fontSize: '0.7rem',
                    fontFamily: '"Calibri", "Arial", sans-serif',
                  },
                }}
              >
                <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: '#4a7bb0' } }}>
                  <TableRow>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', color: '#fff' }}>
                      Sample Point Designation
                    </TableCell>
                    <TableCell rowSpan={2} sx={{ fontWeight: 'bold', color: '#fff' }}>
                      Type
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      align="center"
                      sx={{ fontWeight: 'bold', color: '#fff' }}
                    >
                      Coordinates
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#fff' }}>
                      Easting
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#fff' }}>
                      Northing
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(samplePointData || facilityDetailData?.samplePoints || []).map((point, idx) => (
                    <TableRow key={point.samplePointId || idx} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{point.samplePointName || '-'}</TableCell>
                      <TableCell>{point.measurementName || '-'}</TableCell>
                      <TableCell align="center">{point.coordinates?.latitude || '-'}</TableCell>
                      <TableCell align="center">{point.coordinates?.longitude || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* --- 4. MEASURED 1-HOUR AMBIENT AIR POLLUTANTS CONCENTRATIONS --- */}
        {renderDynamicPollutantTable(
          'Measured 1-Hour Ambient Air Pollutants Concentrations',
          polutantData
        )}

        {/* --- 5. MEASURED 24-HOUR AMBIENT AIR POLLUTANTS CONCENTRATIONS --- */}
        {renderDynamicPollutant24HrTable(
          'Measured 24-Hour Ambient Air Pollutants Concentrations',
          polutant24HrData
        )}

        {/* --- 6. MEASURED AMBIENT NOISE LEVEL --- */}
        {renderDynamicPollutantTable('Measured Ambient Noise Level', noiceLevelData)}

        {/* --- 7. BOREHOLE MEASURED IN SITU PARAMETERS --- */}
        {renderDynamicPollutantTable('Borehole Measured In situ Parameters', boreholeData, [
          'a. Regulatory standard presented here refers to those recommended by WHO and SON, which also reflects NESREA standards.',
          'b. SON recommends maximum temperature not exceeding the ambient temperature.',
          'c. SON recommends maximum turbidity value not exceeding 5 NTU.',
        ])}

        {/* --- 8. STACK EMISSIONS FROM GENERATORS --- */}
        {renderDynamicPollutantTable('Stack Emissions from Generators', generatorData)}

        {/* --- 9. STACK EMISSIONS FROM BOILERS --- */}
        {renderDynamicPollutantTable('Stack Emissions from Boilers', boilerData)}

        {/* --- 10. FALLBACK GROUPED PARAMETER TABLES --- */}
        {/* {Object.keys(groupedData).length > 0 &&
          Object.keys(groupedData).map((groupName, idx) => {
            const points = groupedData[groupName];

            const parameterMap = new Map();
            points.forEach((point) => {
              point.parameters?.forEach((p) => {
                if (!parameterMap.has(p.parameterId)) {
                  parameterMap.set(p.parameterId, {
                    unitName: p.unit || p.parameterId,
                    siUnit: p.siUnit || '',
                    nesrea: p.nesrea || '-',
                    world: p.world || '-',
                  });
                }
              });
            });

            const uniqueParams = Array.from(parameterMap.entries());
            if (uniqueParams.length === 0) return null;

            return (
              <Box key={idx} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    mb: 0.8,
                    textTransform: 'uppercase',
                    color: theme.palette.primary.dark,
                    borderLeft: 3,
                    borderColor: 'primary.main',
                    pl: 0.8,
                    fontSize: '0.75rem',
                  }}
                >
                  {groupName}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table
                    size="small"
                    sx={{
                      border: '1px solid #d0d0d0',
                      '& .MuiTableCell-root': {
                        fontSize: '0.675rem',
                        fontFamily: '"Calibri", "Arial", sans-serif',
                        py: 0.3,
                        px: 0.6,
                        lineHeight: 1.2,
                      },
                    }}
                  >
                    <TableHead sx={{ '& .MuiTableCell-head': { bgcolor: 'grey.100' } }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                          Sample Point
                        </TableCell>
                        {uniqueParams.map(([paramId, meta]) => (
                          <TableCell key={paramId} align="center" sx={{ fontWeight: 'bold' }}>
                            <span dangerouslySetInnerHTML={{ __html: meta.unitName }} />
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.625rem' }}
                        >
                          Unit
                        </TableCell>
                        {uniqueParams.map(([paramId, meta]) => (
                          <TableCell
                            key={paramId}
                            align="center"
                            sx={{ color: 'text.secondary', fontSize: '0.625rem' }}
                          >
                            <span dangerouslySetInnerHTML={{ __html: meta.siUnit }} />
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.625rem' }}
                        >
                          NESREA / World Limit
                        </TableCell>
                        {uniqueParams.map(([paramId, meta]) => (
                          <TableCell
                            key={paramId}
                            align="center"
                            sx={{ fontSize: '0.625rem', color: 'warning.main', fontWeight: 600 }}
                          >
                            {meta.nesrea} / {meta.world}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {points.map((point) => (
                        <TableRow key={point.samplePointId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{point.samplePointName}</TableCell>
                          {uniqueParams.map(([paramId]) => {
                            const paramMatch = point.parameters?.find(
                              (p) => p.parameterId === paramId
                            );
                            const value = getResultValue(point, paramId, paramMatch);
                            const isExceeded = checkIfExceeded(point, paramId, paramMatch);

                            return (
                              <TableCell
                                key={paramId}
                                align="center"
                                sx={{
                                  color: isExceeded ? '#d32f2f' : 'inherit',
                                  fontWeight: isExceeded ? 700 : 400,
                                  bgcolor: isExceeded ? alpha('#d32f2f', 0.08) : 'transparent',
                                }}
                              >
                                {value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })} */}

        {/* --- 11. GENERAL REPORT DISCUSSION SECTION --- */}
        {reportDiscussion && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#ffffff' }}>
            {/* <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <ArticleOutlined color="primary" sx={{ fontSize: '1.1rem' }} />
              <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ fontSize: '0.85rem' }}>
                Report Discussion
              </Typography>
            </Stack> */}
            
            <Box
              sx={{
                '& h1, & h2, & h3, & h4': { color: theme.palette.primary.dark, mt: 1.5, mb: 0.8 },
                '& h1': { fontSize: '1rem', fontWeight: 700 },
                '& h2': { fontSize: '0.9rem', fontWeight: 700 },
                '& h3': { fontSize: '0.825rem', fontWeight: 700 },
                '& p': { fontSize: '0.75rem', lineHeight: 1.5, color: 'text.primary', mb: 0.8 },
                '& ul': { pl: 2.5, mb: 0.8 },
                '& li': { fontSize: '0.75rem', mb: 0.3 },
                '& hr': { my: 1.5, borderColor: 'divider' },
              }}
              dangerouslySetInnerHTML={{ __html: reportDiscussion }}
            />
          </Paper>
        )}

        {/* --- 12. FACILITY ANALYTICS REPORT SECTION --- */}
        {renderAnalyticsReportView()}

        {/* --- 13. RECOMMENDATIONS & CORRECTIVE ACTIONS SECTION --- */}
        {renderRecommendationsReportView()}
      </Box>
    );
  };

  const isFormValid = Boolean(facilitySelected?.facilityId && yearSelected && quarterSelected);

  return (
    <Box sx={{ height: embedded ? '100%' : '90vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        {/* Main Preview Area */}
        <Grid2
          size={embedded ? 12 : { lg: 9, md: 8, sm: 12, xs: 12 }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {/* Top Header Bar */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              Report Preview
            </Typography>

            {hasAnyReportData && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={
                  downloadLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PictureAsPdfOutlined />
                  )
                }
                disabled={downloadLoading}
                onClick={exportToPDF}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                }}
              >
                {downloadLoading ? 'Generating PDF...' : 'Download PDF'}
              </Button>
            )}
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              borderRight: 1,
              borderColor: 'divider',
              overflowY: 'auto',
              bgcolor: '#f8f9fa',
            }}
          >
            {renderReportTables()}
          </Box>
        </Grid2>

        {/* Right Sidebar Controls (hidden when embedded with a preset report) */}
        {!embedded && (
        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '85%' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Preview Controls
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  options={facilityList}
                  getOptionLabel={(option) => option.facilityName || ''}
                  value={facilitySelected?.facilityId ? facilitySelected : null}
                  onChange={(_, value) => setFacilitySelected(value || initFacility)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Facility"
                      size="small"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: facilityLoading ? (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />

                <FormControl fullWidth size="small">
                  <InputLabel>Year *</InputLabel>
                  <Select
                    label="Year *"
                    size="small"
                    value={yearSelected}
                    onChange={(e) => setYearSelected(e.target.value)}
                  >
                    {getYears().map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" disabled={!facilitySelected?.facilityId}>
                  <InputLabel>Quarter *</InputLabel>
                  <Select
                    label="Quarter *"
                    size="small"
                    value={quarterSelected}
                    onChange={(e) => setQuarterSelected(e.target.value)}
                  >
                    {quarterList?.map((y, index) => {
                      const isEdm = userCategory === userCats.EDM;
                      const isQaqc = y?.reportType === reportTypes.QAQC;

                      return (
                        <MenuItem key={index} value={y?.reportQuarter} disabled={isQaqc && !isEdm}>
                          Quarter {y?.reportQuarter}{' '}
                          {isQaqc && (
                            <Chip
                              label={y?.reportType}
                              size="small"
                              color={isEdm ? 'warning' : 'default'}
                              sx={{ ml: 1, fontSize: '0.8rem' }}
                            />
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            <Box
              sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  reportLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <VisibilityOutlined />
                  )
                }
                disabled={reportLoading || !isFormValid}
                onClick={handlePreviewReport}
                sx={{ py: 1.2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                Preview
              </Button>
              {hasAnyReportData && (
                <AddRecommendationModal
                  facilityId={facilitySelected?.facilityId}
                  quarter={quarterSelected}
                  year={yearSelected}
                  onSuccess={() => handlePreviewReport()}
                />
              )}
            </Box>
          </Stack>
        </Grid2>
        )}
      </Grid2>
    </Box>
  );
}
